/**
 * "Talk to the team" — the real one.
 *
 * The old panel was a `mailto:` link dressed as a message box: nothing was
 * stored, nothing was tracked, and "Opened in your mail app" was the entire
 * promise. This posts to `/api/messages`, which persists the message and pushes
 * it to the owner over whatever transport is configured (email today).
 *
 * The thread history is the point. A customer who wrote on Tuesday should be
 * able to see on Thursday that they wrote, what they said, and what came back.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageService, type ThreadMessage } from '../../services/messageService';
import type { SetupStatus } from '../../lib/setupFlow';
import { Eyebrow, Panel, PanelBody, PanelHeader, Tag } from '../ui/kit';
import { cn } from '@/lib/utils';

const SUPPORT_EMAIL = 'hello@foundlio.com';
const MAX_LENGTH = 4000;
/** Open panels re-check for a reply on this cadence. */
const POLL_MS = 30_000;

function timeLabel(iso: string): string {
  const date = new Date(iso);
  const mins = Math.round((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)} hr ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * One message. Not a chat bubble — the portal's language is hairlines and
 * monospace labels, so a message is a labelled block with the team's answers
 * pulled out by an ink rule.
 */
function MessageRow({ message }: { message: ThreadMessage }) {
  const isTeam = message.sender === 'team';

  return (
    <li
      className={cn(
        'border-l-2 py-2.5 pl-3.5',
        isTeam ? 'border-primary' : 'border-zinc-200'
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
          {isTeam ? 'Foundlio' : 'You'}
        </span>
        <span className="font-mono text-[11px] text-zinc-400">{timeLabel(message.createdAt)}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-primary">{message.body}</p>
    </li>
  );
}

export default function MessageTeamPanel({ status }: { status: SetupStatus }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // `?demo=1` runs the whole tracker with no backend. A demo panel must not
  // pretend to send — it says so and stays inert.
  const isDemo = status.isMock;

  const {
    data: thread,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['message-thread'],
    queryFn: messageService.getThread,
    enabled: !isDemo,
    refetchInterval: POLL_MS,
  });

  // Seeing the panel is seeing the replies.
  useEffect(() => {
    if (isDemo || !thread?.unreadCount) return;
    messageService
      .markRead()
      .then(() => queryClient.invalidateQueries({ queryKey: ['message-thread'] }))
      .catch(() => {
        /* a stuck badge is not worth an error state */
      });
  }, [isDemo, thread?.unreadCount, queryClient]);

  // Newest at the bottom, so the history scrolls to it.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [thread?.messages.length]);

  const send = useMutation({
    mutationFn: (body: string) => messageService.send(body),
    onSuccess: () => {
      setDraft('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['message-thread'] });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Could not send that. Try again in a moment.';
      setError(message);
    },
  });

  const messages = thread?.messages ?? [];
  const canSend = draft.trim().length > 0 && draft.length <= MAX_LENGTH && !send.isPending;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSend) return;
    send.mutate(draft.trim());
  };

  const lastFromTeam = messages.length > 0 && messages[messages.length - 1]?.sender === 'team';
  const awaiting = messages.length > 0 && !lastFromTeam;

  return (
    <Panel>
      <PanelHeader
        title="Talk to the team"
        actions={awaiting ? <Tag tone="warn">With us</Tag> : undefined}
      />
      <PanelBody className="space-y-3">
        <p className="text-sm leading-snug text-muted">
          A real person is building this. Ask anything — a change, a question, or just where things
          stand.
        </p>

        {isDemo ? (
          <p className="rounded-lg border border-dashed border-zinc-300 px-3 py-2.5 text-sm text-muted">
            Messaging is off in the demo. On a real account this sends straight to the person
            building your site.
          </p>
        ) : (
          <>
            {isLoading && (
              <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
                Loading your messages…
              </p>
            )}

            {isError && (
              <p className="text-sm text-red-700">
                We couldn&apos;t load your messages. Write to {SUPPORT_EMAIL} and we&apos;ll pick it
                up there.
              </p>
            )}

            {messages.length > 0 && (
              <div
                ref={scrollRef}
                className="max-h-72 space-y-1 overflow-y-auto border-t border-zinc-200 pt-3"
              >
                <Eyebrow className="mb-1">Thread</Eyebrow>
                <ul className="space-y-1">
                  {messages.map((message) => (
                    <MessageRow key={message.id} message={message} />
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <textarea
                value={draft}
                onChange={(event) => {
                  setDraft(event.target.value);
                  if (error) setError(null);
                }}
                rows={3}
                maxLength={MAX_LENGTH}
                disabled={send.isPending}
                placeholder={
                  messages.length
                    ? 'Write another message…'
                    : 'e.g. Can you swap the hero photo for one of our trucks?'
                }
                className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-primary outline-none transition-colors placeholder:text-zinc-400 focus:border-primary disabled:opacity-60"
              />

              {error && <p className="text-sm text-red-700">{error}</p>}

              <button
                type="submit"
                disabled={!canSend}
                className={cn(
                  'block w-full rounded-lg bg-primary px-4 py-2.5 text-center text-sm font-semibold text-white transition-opacity',
                  canSend ? 'hover:opacity-90' : 'cursor-not-allowed opacity-40'
                )}
              >
                {send.isPending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          </>
        )}

        <p className="text-center font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
          {send.isSuccess && !error
            ? 'Sent · we get it straight away'
            : `Replies within a few hours · ${SUPPORT_EMAIL}`}
        </p>
      </PanelBody>
    </Panel>
  );
}
